import React, {
  useRef,
  Component,
  Suspense,
  lazy,
  type ReactNode,
} from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import type { ChartArea, ScriptableContext, TooltipItem } from "chart.js";
import { Line } from "react-chartjs-2";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

export interface PerformanceChartProps {
  height: number | undefined;
  data?: number[];
  labels?: string[];
  title?: string;
  subtitle?: string;
  currentValue?: number;
  className?: string;
}

// Error Boundary interfaces
interface ChartErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  className?: string;
}

interface ChartErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

// Chart Error Boundary Component
class ChartErrorBoundary extends Component<
  ChartErrorBoundaryProps,
  ChartErrorBoundaryState
> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ChartErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Chart Error Boundary caught an error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className={cn(
            "bg-white rounded-2xl p-6 shadow-sm border border-neutral-200 w-full",
            "flex flex-col items-center justify-center min-h-[200px]",
            this.props.className,
          )}
        >
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-3 bg-red-50 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-body-medium font-semibold text-neutral-900">
                Không thể tải biểu đồ
              </h3>
              <p className="text-body-small text-neutral-600 max-w-sm">
                Đã xảy ra lỗi khi tải biểu đồ. Vui lòng thử lại.
              </p>
            </div>

            <button
              onClick={this.handleRetry}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2",
                "bg-mebi-blue-600 hover:bg-mebi-blue-700",
                "text-white text-body-small font-medium",
                "rounded-lg transition-colors duration-200",
                "focus:outline-none focus:ring-2 focus:ring-mebi-blue-500 focus:ring-offset-2",
              )}
            >
              <RefreshCw className="w-4 h-4" />
              Thử lại
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading Skeleton Component
const ChartSkeleton: React.FC<{ height?: number; className?: string }> = ({
  height = 222,
  className,
}) => (
  <div
    className={cn(
      "bg-white rounded-2xl p-4 shadow-sm",
      "flex flex-col justify-center items-center",
      "border border-neutral-200",
      className,
    )}
    style={{ height }}
  >
    <div className="w-full max-w-sm mx-auto space-y-4">
      {/* Title skeleton */}
      <div className="space-y-2">
        <div className="h-4 bg-neutral-200 rounded-md w-32 mx-auto animate-pulse" />
        <div className="h-3 bg-neutral-200 rounded-md w-24 mx-auto animate-pulse" />
      </div>
      {/* Chart area skeleton */}
      <div className="h-32 bg-neutral-100 rounded-lg relative overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -skew-x-12"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
            animation: "shimmer 2s infinite ease-in-out",
          }}
        />
      </div>

      {/* Stats skeleton */}
      <div className="flex justify-between items-center">
        <div className="h-3 bg-neutral-200 rounded-md w-16 animate-pulse" />
        <div className="h-4 bg-neutral-200 rounded-md w-12 animate-pulse" />
      </div>
    </div>
  </div>
);

// Core Chart Component
const PerformanceChartCore: React.FC<PerformanceChartProps> = ({
  height,
  data = [1650, 1680, 1720, 1750, 1700, 1780, 1820, 1900],
  labels = ["T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8"],
  title = "Biểu đồ ELO",
  subtitle = "Xu hướng điểm số",
  currentValue = 1900,
  className,
}) => {
  const chartRef = useRef<ChartJS<"line", number[], string>>(null);

  // Calculate percentage change
  const calculateChange = () => {
    if (data.length < 2) return { value: 0, isPositive: true };
    const firstValue = data[0];
    const lastValue = data[data.length - 1];
    const change = ((lastValue - firstValue) / firstValue) * 100;
    return {
      value: Math.abs(change).toFixed(1),
      isPositive: change >= 0,
    };
  };

  const change = calculateChange();

  // Create gradient
  const createGradient = (ctx: CanvasRenderingContext2D, area: ChartArea) => {
    const gradient = ctx.createLinearGradient(0, area.bottom, 0, area.top);
    gradient.addColorStop(0, "rgba(240, 187, 68, 0.05)");
    gradient.addColorStop(1, "rgba(240, 187, 68, 0.3)");
    return gradient;
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: "ELO Rating",
        data: data,
        borderColor: "#f0bb44",
        backgroundColor: (context: ScriptableContext<"line">) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return "rgba(240, 187, 68, 0.1)";
          }
          return createGradient(ctx, chartArea);
        },
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#f0bb44",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: "#ffffff",
        pointHoverBorderColor: "#f0bb44",
        pointHoverBorderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#ffffff",
        titleColor: "#111827",
        bodyColor: "#111827",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        titleFont: {
          size: 12,
          weight: 500 as const,
        },
        bodyFont: {
          size: 12,
        },
        padding: 12,
        callbacks: {
          title: function (context: TooltipItem<"line">[]) {
            return context[0].label;
          },
          label: function (context: TooltipItem<"line">) {
            return `${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: "#9ca3af",
          font: {
            size: 12,
          },
        },
      },
      y: {
        display: false,
        min: Math.min(...data) - 50,
        max: Math.max(...data) + 50,
      },
    },
    elements: {
      point: {
        hoverRadius: 6,
      },
    },
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
  };

  return (
    <div
      className={cn(
        "bg-white rounded-2xl p-4 shadow-sm border border-neutral-200 w-full",
        className,
      )}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-body-medium font-semibold text-neutral-900 mb-1">
            {title}
          </h3>
          <p className="text-body-small text-neutral-600">{subtitle}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-neutral-900">
            {currentValue}
          </div>
          <div
            className={cn(
              "text-body-small font-medium flex items-center gap-1",
              change.isPositive ? "text-mebi-green-600" : "text-mebi-red-600",
            )}
          >
            <span>{change.isPositive ? "↗" : "↘"}</span>
            {change.value}%
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="w-full h-48" style={height ? { height } : undefined}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
};

// Lazy-loaded Chart Component with Error Boundary
const LazyPerformanceChart = lazy(() =>
  Promise.resolve({ default: PerformanceChartCore }),
);

/**
 * PerformanceChart - Main chart component with lazy loading and error boundary
 *
 * This component combines error boundary, lazy loading, and skeleton loading
 * for optimal performance and user experience.
 *
 * @example
 * ```tsx
 * <PerformanceChart
 *   data={[1650, 1680, 1720]}
 *   labels={['Jan', 'Feb', 'Mar']}
 *   title="ELO Chart"
 * />
 * ```
 */
export const PerformanceChart: React.FC<PerformanceChartProps> = (props) => {
  return (
    <ChartErrorBoundary className={props.className}>
      <Suspense
        fallback={
          <ChartSkeleton height={props.height} className={props.className} />
        }
      >
        <LazyPerformanceChart {...props} />
      </Suspense>
    </ChartErrorBoundary>
  );
};

// Export for backward compatibility
export const PerformanceChartLazy = PerformanceChart;
export { ChartErrorBoundary };
export default PerformanceChart;
